import { Box } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React from "react";
import {
  NotificationType,
  SocketNotificationType,
} from "../constants/appConstants";
import BookingStarted from "../components/socketNotification/BookingStarted";
import DefaultNotification from "../components/socketNotification/DefaultNotification";
import BookingAcceptedRejected from "../components/socketNotification/BookingAcceptedRejected";
import BookingEnded from "../components/socketNotification/BookingEnded";
import CoffeeCallStarted from "../components/socketNotification/CoffeeCallStarted";
import CoffeeCallAccepted from "../components/socketNotification/CoffeeCallAccepted";
import CoffeeCallRejected from "../components/socketNotification/CoffeeCallRejected";
import CoffeeCallEnded from "../components/socketNotification/CoffeeCallEnded";
import BookingInvite from "../components/socketNotification/BookingInvite";
import BookingStart from "../components/socketNotification/BookingStart";
import BookingCreated from "../components/socketNotification/BookingCreated";
import { NotificationFullData } from "./UserNotification";
import useThemeStore from "../services/store/useThemeStore";
import CoffeeCallCreated from "../components/socketNotification/CoffeeCallCreated";
import CloverCardPurchased from "../components/socketNotification/CloverCardPurchased";
import DiamondCardPurchased from "../components/socketNotification/DiamondCardPurchased";
import CloverCardJoin from "../components/socketNotification/CloverCardJoin";
import QuestionAsked from "../components/socketNotification/QuestionAsked";
import AceLoungeCardBooked from "../components/socketNotification/AceLoungeCardBooked";
import AceLoungeAcceptReject from "../components/socketNotification/AceLoungAcceptReject";
import PremiumCardPurchased from "../components/socketNotification/PremiumCardPurchased";
import { useTranslation } from "react-i18next";
import FlashCardBookingNotification from "../components/socketNotification/FlashCardBookingNotification";
import FlashCardRefundNotification from "../components/socketNotification/FlashCardRefundNotification";
import { useCurrentTalentProfile } from "../hooks";
import StaiSessionPurchased from "../components/socketNotification/StaiSessionPurchased";
import StaiReaction from "../components/socketNotification/StaiReaction";
import StaiComment from "../components/socketNotification/StaiComment";
import StaiCommentReply from "../components/socketNotification/StaiCommentReply";

interface SocketNotificationProps {
  notification: NotificationFullData;
  closePopover: () => void;
}

const styles = (darkMode: boolean) => ({
  wrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    textAlign: "left",
    justifyContent: "space-between",
    border: darkMode ? "1px solid #333" : "1px solid #E5E7EB",
    borderRadius: "10px",
    background: darkMode ? "#121212" : "#F9FAFB",
    width: "100%",
    boxShadow: darkMode
      ? "0 2px 8px rgba(0,0,0,0.04)"
      : "0 2px 8px rgba(0,0,0,0.04)",
  },
  button: {
    background: darkMode ? "var(--button-gradient)" : "var(--button-gradient)",
    color: darkMode ? "var(--neutralWhite)" : "var(--neutralWhite)",
    textTransform: "none",
    font: "var(--semibold-xxs)",
    padding: "6px 18px",
    boxShadow: darkMode ? "none" : "none",
    minWidth: "85px",
    borderRadius: "8px !important",
    "&:hover": {
      background: darkMode
        ? "var(--button-gradient-hover)"
        : "var(--button-gradient-hover)",
    },
    mt: 1,
    alignSelf: "flex-end",
  },
});

const SocketNotification: React.FC<SocketNotificationProps> = ({
  notification,
  closePopover,
}) => {
  const { darkMode } = useThemeStore();
  const { t } = useTranslation();
  const { currentProfile } = useCurrentTalentProfile();

  const renderNotification = (notification: NotificationFullData) => {
    switch (notification?.notification_type) {
      case NotificationType.BookingAccepted:
        return <BookingAcceptedRejected notification={notification} />;
      // case NotificationType.BookingRejected:
      //   return <BookingAcceptedRejected notification={notification} />;
      case SocketNotificationType.BookingStarted:
        return <BookingStarted notification={notification} />;
      case SocketNotificationType.BookingEnded:
        return <BookingEnded notification={notification} />;
      case SocketNotificationType.CoffeeCallStarted:
        return <CoffeeCallStarted notification={notification} />;
      case SocketNotificationType.CoffeeCallAccepted:
        return <CoffeeCallAccepted notification={notification} />;
      case SocketNotificationType.CoffeeCallRejected:
        return <CoffeeCallRejected notification={notification} />;
      case SocketNotificationType.CoffeeCallEnded:
        return <CoffeeCallEnded notification={notification} />;
      case NotificationType.BookingCreated:
        return <BookingCreated notification={notification} />;
      case NotificationType.CardQuestionCreated:
        return <QuestionAsked notification={notification} />;
      case NotificationType.CardAnswerCreated:
        return <QuestionAsked notification={notification} />;
      case SocketNotificationType.BookingInvite:
        return <BookingInvite notification={notification} />;
      case SocketNotificationType.BookingStart:
        return <BookingStart notification={notification} />;
      case NotificationType.CloverCardStarted:
        return <CloverCardJoin notification={notification} />;
      case NotificationType.CoffeeCallCreated:
        return <CoffeeCallCreated notification={notification} />;
      case NotificationType.CloverTicketPurchased:
        return <CloverCardPurchased notification={notification} />;
      case NotificationType.AceLoungeBookingCreated:
        return <AceLoungeCardBooked notification={notification} />;
      case NotificationType.AceLoungeBookingAccepted:
        return <AceLoungeAcceptReject notification={notification} />;
      case NotificationType.AceLoungeBookingRejected:
        return <AceLoungeAcceptReject notification={notification} />;
      case NotificationType.PremiumCardPurchased:
        return <PremiumCardPurchased notification={notification} />;
      case NotificationType.DiamondCardProductPurchased:
        return <DiamondCardPurchased notification={notification} />;
      case NotificationType.FlashCardBooking:
        return <FlashCardBookingNotification notification={notification} />;
      case NotificationType.FlashCardBookingRefund:
        if (!currentProfile?.id) {
          return <FlashCardRefundNotification notification={notification} />;
        }
        return <FlashCardRefundNotification notification={notification} />;
      case NotificationType.StaiSessionPurchased:
        return <StaiSessionPurchased notification={notification} />;
      case NotificationType.StaiReaction:
        return <StaiReaction notification={notification} />;
      case NotificationType.StaiComment:
        return <StaiComment notification={notification} />;
      case NotificationType.StaiCommentReply:
        return <StaiCommentReply notification={notification} />;
      default:
        return <DefaultNotification notification={notification} />;
    }
  };

  const handleClick = () => {
    try {
      closePopover();
    } catch (error) {
      console.error("Navigation error:", error);
      enqueueSnackbar("Failed to navigate to the requested page.", {
        variant: "error",
      });
    }
  };

  const translatedTitle = t(`${notification.notification_type}.title`, {
    ns: "notification",
  });
  const translatedMessage = t(`${notification.notification_type}.message`, {
    ns: "notification",
  });

  // Use original message if translation doesn't exist (when t() returns the key itself)
  const messageKey = `${notification.notification_type}.message`;
  const finalMessage =
    translatedMessage === messageKey ? notification.message : translatedMessage;

  // Use original title if translation doesn't exist
  const titleKey = `${notification.notification_type}.title`;
  const finalTitle =
    translatedTitle === titleKey ? notification.title : translatedTitle;

  return (
    <Box onClick={handleClick} sx={styles(darkMode).wrapper}>
      {renderNotification({
        ...notification,
        title: finalTitle,
        message: finalMessage,
      })}
    </Box>
  );
};

export default SocketNotification;
